import type { VariantProp } from '../../common/colors';
import type { CSS } from '../../stitches.config';
import { fade, palette } from '../../common/colors';
import { gradients } from '../../common/gradients';
import { outlineColors, outlines } from '../../common/outlines';
import { colors, darkThemeSelector, shadows } from '../../stitches.config';
import { selectors } from '../shared/styles';

export const labelColor = '$$labelColor';
export const iconColor = '$$iconColor';
export const arrowsColor = '$$arrowsColor';
export const loadingIconColor = '$$loadingIconColor';

const secondaryCustomColors = {
  // Custom initial color for secondary dark buttons
  initial: '#2E3046',
  // Custom interactive color for secondary dark buttons
  interactive: '#32344C',
};

export const destructiveVividEnabledStyles = {
  backgroundColor: colors.controlBgVividDestructiveInitialLight,
  outline: outlines.control,
  outlineColor: outlineColors.control.destructiveVividInitialLight,
  boxShadow: shadows.controlVividDestructiveInitialLight,
  [labelColor]: colors.red50,
  [iconColor]: colors.red100,
  [arrowsColor]: colors.red200,
  [loadingIconColor]: colors.red300,

  [darkThemeSelector]: {
    backgroundColor: colors.controlBgVividDestructiveInitialDark,
    outlineColor: outlineColors.control.destructiveVividInitialDark,
    boxShadow: shadows.controlVividDestructiveInitialDark,
  },

  [selectors.hover]: {
    backgroundColor: colors.controlBgVividDestructiveHoveredLight,
    outlineColor: outlineColors.control.destructiveVividHoveredLight,
    boxShadow: shadows.controlVividDestructiveHoveredLight,
    [labelColor]: colors.white,
    [iconColor]: colors.red50,
    [arrowsColor]: colors.red200,

    [darkThemeSelector]: {
      backgroundColor: colors.controlBgVividDestructiveHoveredDark,
      outlineColor: outlineColors.control.destructiveVividHoveredDark,
      boxShadow: shadows.controlVividDestructiveHoveredDark,
    },
  },

  [selectors.focus]: {
    backgroundColor: colors.controlBgVividDestructiveFocusedLight,
    outlineColor: outlineColors.control.destructiveVividFocusedLight,
    boxShadow: shadows.controlVividDestructiveFocusedLight,

    [darkThemeSelector]: {
      backgroundColor: colors.controlBgVividDestructiveFocusedDark,
      outlineColor: outlineColors.control.destructiveVividFocusedDark,
      boxShadow: shadows.controlVividDestructiveFocusedDark,
    },
  },
};

export const destructiveVividDisabledStyles = {
  backgroundColor: colors.controlBgVividDestructiveDisabledLight,
  outline: outlines.control,
  outlineColor: outlineColors.control.destructiveVividDisabledLight,
  boxShadow: shadows.controlVividDestructiveDisabledLight,
  [labelColor]: colors.red200,
  [iconColor]: colors.red300,
  [arrowsColor]: colors.red400,
  [loadingIconColor]: colors.red400,

  [darkThemeSelector]: {
    outlineColor: outlineColors.control.destructiveVividDisabledDark,
    boxShadow: shadows.controlVividDestructiveDisabledDark,
  },
};

export const destructiveMutedEnabledStyles = {
  backgroundColor: fade(palette.controlBgMutedDestructiveLight, 0.5),
  outline: outlines.control,
  outlineColor: outlineColors.control.destructiveMutedInitialLight,
  [labelColor]: colors.red800,
  [iconColor]: colors.red700,
  [arrowsColor]: colors.red600,
  [loadingIconColor]: colors.red700,

  [darkThemeSelector]: {
    backgroundColor: fade(palette.controlBgMutedDestructiveDark, 0.5),
    outlineColor: outlineColors.control.destructiveMutedInitialDark,
    [labelColor]: colors.red50,
    [iconColor]: colors.red100,
    [arrowsColor]: colors.red200,
    [loadingIconColor]: colors.red200,
  },

  [selectors.hover]: {
    backgroundColor: fade(palette.controlBgMutedDestructiveLight, 0.9),
    outlineColor: outlineColors.control.destructiveMutedHoveredLight,
    [labelColor]: colors.red900,
    [iconColor]: colors.red800,
    [arrowsColor]: colors.red700,
    [loadingIconColor]: colors.red700,

    [darkThemeSelector]: {
      backgroundColor: fade(palette.controlBgMutedDestructiveDark, 0.7),
      outlineColor: outlineColors.control.destructiveMutedHoveredDark,
      [labelColor]: colors.white,
      [iconColor]: colors.red50,
      [arrowsColor]: colors.red100,
      [loadingIconColor]: colors.red100,
    },
  },

  [selectors.focus]: {
    backgroundColor: fade(palette.controlBgMutedDestructiveLight, 0.9),
    outlineColor: outlineColors.control.destructiveMutedFocusedLight,
    boxShadow: shadows.controlMutedDestructiveFocusedLight,
    [labelColor]: colors.red900,
    [iconColor]: colors.red800,
    [arrowsColor]: colors.red700,
    [loadingIconColor]: colors.red700,

    [darkThemeSelector]: {
      backgroundColor: fade(palette.controlBgMutedDestructiveDark, 0.7),
      outlineColor: outlineColors.control.destructiveMutedFocusedDark,
      boxShadow: shadows.controlMutedDestructiveFocusedDark,
      [labelColor]: colors.white,
      [iconColor]: colors.red50,
      [arrowsColor]: colors.red100,
      [loadingIconColor]: colors.red100,
    },
  },
};

export const destructiveMutedDisabledStyles = {
  backgroundColor: fade(palette.controlBgMutedDestructiveLight, 0.5),
  outline: outlines.control,
  outlineColor: outlineColors.control.destructiveMutedDisabledLight,
  [labelColor]: colors.red800,
  [iconColor]: colors.red700,
  [arrowsColor]: colors.red600,
  [loadingIconColor]: colors.red600,

  [darkThemeSelector]: {
    backgroundColor: fade(palette.controlBgMutedDestructiveDark, 0.5),
    outlineColor: outlineColors.control.destructiveMutedDisabledDark,
    [labelColor]: colors.red50,
    [iconColor]: colors.red100,
    [arrowsColor]: colors.red200,
    [loadingIconColor]: colors.red200,
  },
};

export const primaryVividEnabledStyles = {
  backgroundColor: colors.controlBgVividPrimaryInitialLight,
  outline: outlines.control,
  outlineColor: outlineColors.control.primaryVividInitialLight,
  boxShadow: shadows.controlVividPrimaryInitialLight,
  [labelColor]: colors.brand50,
  [iconColor]: colors.brand100,
  [arrowsColor]: colors.brand200,
  [loadingIconColor]: colors.brand300,

  [darkThemeSelector]: {
    backgroundColor: colors.controlBgVividPrimaryInitialDark,
    outlineColor: outlineColors.control.primaryVividInitialDark,
    boxShadow: shadows.controlVividPrimaryInitialDark,
  },

  [selectors.hover]: {
    backgroundColor: colors.controlBgVividPrimaryHoveredLight,
    outlineColor: outlineColors.control.primaryVividHoveredLight,
    boxShadow: shadows.controlVividPrimaryHoveredLight,
    [labelColor]: colors.white,
    [iconColor]: colors.brand50,
    [arrowsColor]: colors.brand100,
    [loadingIconColor]: colors.brand100,

    [darkThemeSelector]: {
      backgroundColor: colors.controlBgVividPrimaryHoveredDark,
      outlineColor: outlineColors.control.primaryVividHoveredDark,
      boxShadow: shadows.controlVividPrimaryHoveredDark,
    },
  },

  [selectors.focus]: {
    backgroundColor: colors.controlBgVividPrimaryFocusedLight,
    outlineColor: outlineColors.control.primaryVividFocusedLight,
    boxShadow: shadows.controlVividPrimaryFocusedLight,

    [darkThemeSelector]: {
      backgroundColor: colors.controlBgVividPrimaryFocusedDark,
      outlineColor: outlineColors.control.primaryVividFocusedDark,
      boxShadow: shadows.controlVividPrimaryFocusedDark,
    },
  },
};

export const primaryVividDisabledStyles = {
  backgroundColor: colors.controlBgVividPrimaryDisabledLight,
  outline: outlines.control,
  outlineColor: outlineColors.control.primaryVividDisabledLight,
  boxShadow: shadows.controlVividPrimaryDisabledLight,
  [labelColor]: colors.brand200,
  [iconColor]: colors.brand300,
  [arrowsColor]: colors.brand400,
  [loadingIconColor]: colors.brand400,

  [darkThemeSelector]: {
    backgroundColor: colors.controlBgVividPrimaryDisabledDark,
    outlineColor: outlineColors.control.primaryVividDisabledDark,
    boxShadow: shadows.controlVividPrimaryDisabledDark,
  },
};

export const primaryMutedEnabledStyles = {
  backgroundColor: fade(palette.controlBgMutedPrimaryLight, 0.5),
  outline: outlines.control,
  outlineColor: outlineColors.control.primaryMutedInitialLight,
  [labelColor]: colors.brand800,
  [iconColor]: colors.brand700,
  [arrowsColor]: colors.brand600,
  [loadingIconColor]: colors.brand600,

  [darkThemeSelector]: {
    backgroundColor: fade(palette.controlBgMutedPrimaryDark, 0.5),
    outlineColor: outlineColors.control.primaryMutedInitialDark,
    [labelColor]: colors.brand50,
    [iconColor]: colors.brand100,
    [arrowsColor]: colors.brand200,
    [loadingIconColor]: colors.brand200,
  },

  [selectors.hover]: {
    backgroundColor: fade(palette.controlBgMutedPrimaryLight, 0.9),
    outlineColor: outlineColors.control.primaryMutedHoveredLight,
    [labelColor]: colors.brand900,
    [iconColor]: colors.brand800,
    [arrowsColor]: colors.brand700,
    [loadingIconColor]: colors.brand700,

    [darkThemeSelector]: {
      backgroundColor: fade(palette.controlBgMutedPrimaryDark, 0.7),
      outlineColor: outlineColors.control.primaryMutedHoveredDark,
      [labelColor]: colors.white,
      [iconColor]: colors.brand50,
      [arrowsColor]: colors.brand100,
      [loadingIconColor]: colors.brand100,
    },
  },

  [selectors.focus]: {
    backgroundColor: fade(palette.controlBgMutedPrimaryLight, 0.9),
    outlineColor: outlineColors.control.primaryMutedFocusedLight,
    boxShadow: shadows.controlMutedPrimaryFocusedLight,
    [labelColor]: colors.brand900,
    [iconColor]: colors.brand800,
    [arrowsColor]: colors.brand700,
    [loadingIconColor]: colors.brand700,

    [darkThemeSelector]: {
      backgroundColor: fade(palette.controlBgMutedPrimaryDark, 0.7),
      outlineColor: outlineColors.control.primaryMutedFocusedDark,
      boxShadow: shadows.controlMutedPrimaryFocusedDark,
      [labelColor]: colors.white,
      [iconColor]: colors.brand50,
      [arrowsColor]: colors.brand100,
      [loadingIconColor]: colors.brand100,
    },
  },
};

export const primaryMutedDisabledStyles = {
  backgroundColor: fade(palette.controlBgMutedPrimaryLight, 0.5),
  outline: outlines.control,
  outlineColor: outlineColors.control.primaryMutedDisabledLight,
  [labelColor]: colors.brand800,
  [iconColor]: colors.brand700,
  [arrowsColor]: colors.brand600,
  [loadingIconColor]: colors.brand600,

  [darkThemeSelector]: {
    backgroundColor: fade(palette.controlBgMutedPrimaryDark, 0.5),
    outlineColor: outlineColors.control.primaryMutedDisabledDark,
    [labelColor]: colors.brand50,
    [iconColor]: colors.brand100,
    [arrowsColor]: colors.brand200,
    [loadingIconColor]: colors.brand200,
  },
};

export const secondaryVividEnabledStyles = {
  backgroundColor: palette.white,
  backgroundImage: gradients.controlSecondary,
  outline: outlines.control,
  outlineColor: outlineColors.control.secondaryVividInitialLight,
  boxShadow: shadows.controlSecondaryVividInitialLight,
  [labelColor]: colors.gray700,
  [iconColor]: colors.gray600,
  [arrowsColor]: colors.gray500,
  [loadingIconColor]: colors.gray500,

  [darkThemeSelector]: {
    backgroundColor: secondaryCustomColors.initial,
    backgroundImage: gradients.controlSecondaryDark,
    outlineColor: outlineColors.control.secondaryVividInitialDark,
    boxShadow: shadows.controlSecondaryVividInitialDark,
    [labelColor]: colors.gray50,
    [iconColor]: colors.gray100,
    [arrowsColor]: colors.gray200,
    [loadingIconColor]: colors.gray200,
  },

  [selectors.hover]: {
    backgroundColor: palette.gray50,
    outlineColor: outlineColors.control.secondaryVividHoveredLight,
    boxShadow: shadows.controlSecondaryVividHoveredLight,
    [labelColor]: colors.gray800,
    [iconColor]: colors.gray700,
    [arrowsColor]: colors.gray600,
    [loadingIconColor]: colors.gray600,

    [darkThemeSelector]: {
      backgroundColor: secondaryCustomColors.interactive,
      outlineColor: outlineColors.control.secondaryVividHoveredDark,
      boxShadow: shadows.controlSecondaryVividHoveredDark,
      [labelColor]: colors.white,
      [iconColor]: colors.gray50,
      [arrowsColor]: colors.gray100,
      [loadingIconColor]: colors.gray100,
    },
  },

  [selectors.focus]: {
    outlineColor: outlineColors.control.secondaryVividFocusedLight,
    boxShadow: shadows.controlSecondaryVividFocusedLight,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.secondaryVividFocusedDark,
      boxShadow: shadows.controlSecondaryVividFocusedDark,
    },
  },
};

export const secondaryVividDisabledStyles = {
  backgroundColor: palette.white,
  backgroundImage: gradients.controlSecondary,
  outline: outlines.control,
  outlineColor: outlineColors.control.secondaryVividDisabledLight,
  boxShadow: shadows.controlSecondaryVividDisabledLight,
  [labelColor]: colors.gray400,
  [iconColor]: colors.gray300,
  [arrowsColor]: colors.gray200,
  [loadingIconColor]: colors.gray200,

  [darkThemeSelector]: {
    backgroundColor: colors.gray700,
    backgroundImage: gradients.controlSecondaryDark,
    outlineColor: outlineColors.control.secondaryVividDisabledDark,
    boxShadow: shadows.controlSecondaryVividDisabledDark,
    [labelColor]: colors.gray200,
    [iconColor]: colors.gray300,
    [arrowsColor]: colors.gray400,
    [loadingIconColor]: colors.gray400,
  },
};

export const secondaryMutedEnabledStyles = {
  backgroundColor: colors.bgApplicationLight,
  outline: outlines.control,
  outlineColor: outlineColors.control.secondaryMutedInitialLight,
  [labelColor]: colors.gray600,
  [iconColor]: colors.gray500,
  [arrowsColor]: colors.gray400,
  [loadingIconColor]: colors.gray400,

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    outlineColor: outlineColors.control.secondaryMutedInitialDark,
    [labelColor]: colors.gray50,
    [iconColor]: colors.gray100,
    [arrowsColor]: colors.gray200,
    [loadingIconColor]: colors.gray200,
  },

  [selectors.hover]: {
    backgroundColor: colors.bgNeutralLight,
    outlineColor: outlineColors.control.secondaryMutedHoveredLight,
    [labelColor]: colors.gray800,
    [iconColor]: colors.gray700,
    [arrowsColor]: colors.gray600,
    [loadingIconColor]: colors.gray600,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
      outlineColor: outlineColors.control.secondaryMutedHoveredDark,
      [labelColor]: colors.white,
      [iconColor]: colors.gray50,
      [arrowsColor]: colors.gray100,
      [loadingIconColor]: colors.gray100,
    },
  },

  [selectors.focus]: {
    backgroundColor: colors.bgNeutralLight,
    outlineColor: outlineColors.control.secondaryMutedFocusedLight,
    boxShadow: shadows.controlSecondaryMutedFocusedLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
      outlineColor: outlineColors.control.secondaryMutedFocusedDark,
      boxShadow: shadows.controlSecondaryMutedFocusedDark,
    },
  },
};

export const secondaryMutedDisabledStyles = {
  backgroundColor: colors.bgApplicationLight,
  outline: outlines.control,
  outlineColor: outlineColors.control.secondaryMutedDisabledLight,
  [labelColor]: colors.gray400,
  [iconColor]: colors.gray300,
  [arrowsColor]: colors.gray500,
  [loadingIconColor]: colors.gray500,

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    outlineColor: outlineColors.control.secondaryMutedDisabledDark,
    [labelColor]: colors.gray200,
    [iconColor]: colors.gray300,
    [arrowsColor]: colors.gray400,
    [loadingIconColor]: colors.gray400,
  },
};

export const internalEnabledStyles = {
  outline: outlines.control,
  outlineColor: outlineColors.control.internalInitialLight,
  [labelColor]: colors.internalBodyLight,
  [iconColor]: colors.internalIconLight,
  [arrowsColor]: colors.internalIconLight,
  [loadingIconColor]: colors.internalIconLight,

  [darkThemeSelector]: {
    outlineColor: outlineColors.control.internalInitialDark,
    [labelColor]: colors.internalBodyDark,
    [iconColor]: colors.internalIconDark,
    [arrowsColor]: colors.internalIconDark,
    [loadingIconColor]: colors.internalIconDark,
  },

  [selectors.hover]: {
    outlineColor: outlineColors.control.internalHoveredLight,
    [labelColor]: colors.internalHeadingLight,
    [iconColor]: colors.internalBodyLight,
    [arrowsColor]: colors.internalBodyLight,
    [loadingIconColor]: colors.internalBodyLight,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.internalHoveredDark,
      [labelColor]: colors.internalHeadingDark,
      [iconColor]: colors.internalBodyDark,
      [arrowsColor]: colors.internalBodyDark,
      [loadingIconColor]: colors.internalBodyDark,
    },
  },

  [selectors.focus]: {
    outlineColor: outlineColors.control.internalFocusedLight,
    boxShadow: shadows.controlInternalFocusedLight,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.internalFocusedDark,
      boxShadow: shadows.controlInternalFocusedDark,
    },
  },
};

export const internalDisabledStyles = {
  outline: outlines.control,
  outlineColor: outlineColors.control.internalDisabledLight,
  [labelColor]: colors.internalBodyLight,
  [iconColor]: colors.internalIconLight,
  [arrowsColor]: colors.internalIconLight,
  [loadingIconColor]: colors.internalIconLight,

  [darkThemeSelector]: {
    outlineColor: outlineColors.control.internalDisabledDark,
    [labelColor]: colors.internalBodyDark,
    [iconColor]: colors.internalIconDark,
    [arrowsColor]: colors.internalIconDark,
    [loadingIconColor]: colors.internalIconDark,
  },
};

const interiorVariantColors: Record<
  VariantProp,
  {
    labelLight: string;
    iconLight: string;
    labelHoverLight: string;
    iconHoverLight: string;
    labelDark: string;
    iconDark: string;
    labelHoverDark: string;
    iconHoverDark: string;
  }
> = {
  alternative: {
    labelLight: palette.bodyAlternativeLight,
    iconLight: palette.iconAlternativeLight,
    labelHoverLight: palette.headingAlternativeLight,
    iconHoverLight: palette.bodyAlternativeLight,
    labelDark: palette.bodyAlternativeDark,
    iconDark: palette.iconAlternativeDark,
    labelHoverDark: palette.headingAlternativeDark,
    iconHoverDark: palette.bodyAlternativeDark,
  },
  attention: {
    labelLight: palette.bodyAttentionLight,
    iconLight: palette.iconAttentionLight,
    labelHoverLight: palette.headingAttentionLight,
    iconHoverLight: palette.bodyAttentionLight,
    labelDark: palette.bodyAttentionDark,
    iconDark: palette.iconAttentionDark,
    labelHoverDark: palette.headingAttentionDark,
    iconHoverDark: palette.bodyAttentionDark,
  },
  brand: {
    labelLight: palette.bodyBrandLight,
    iconLight: palette.iconBrandLight,
    labelHoverLight: palette.headingBrandLight,
    iconHoverLight: palette.bodyBrandLight,
    labelDark: palette.bodyBrandDark,
    iconDark: palette.iconBrandDark,
    labelHoverDark: palette.headingBrandDark,
    iconHoverDark: palette.bodyBrandDark,
  },
  negative: {
    labelLight: palette.bodyNegativeLight,
    iconLight: palette.iconNegativeLight,
    labelHoverLight: palette.headingNegativeLight,
    iconHoverLight: palette.bodyNegativeLight,
    labelDark: palette.bodyNegativeDark,
    iconDark: palette.iconNegativeDark,
    labelHoverDark: palette.headingNegativeDark,
    iconHoverDark: palette.bodyNegativeDark,
  },
  neutral: {
    labelLight: palette.bodyNeutralLight,
    iconLight: palette.iconNeutralLight,
    labelHoverLight: palette.headingNeutralLight,
    iconHoverLight: palette.bodyNeutralLight,
    labelDark: palette.bodyNeutralDark,
    iconDark: palette.iconNeutralDark,
    labelHoverDark: palette.headingNeutralDark,
    iconHoverDark: palette.bodyNeutralDark,
  },
  positive: {
    labelLight: palette.bodyPositiveLight,
    iconLight: palette.iconPositiveLight,
    labelHoverLight: palette.headingPositiveLight,
    iconHoverLight: palette.bodyPositiveLight,
    labelDark: palette.bodyPositiveDark,
    iconDark: palette.iconPositiveDark,
    labelHoverDark: palette.headingPositiveDark,
    iconHoverDark: palette.bodyPositiveDark,
  },
};

export function getInteriorVariantStyles(variant: VariantProp): CSS {
  const v = interiorVariantColors[variant];
  return {
    [labelColor]: v.labelLight,
    [iconColor]: v.iconLight,

    [darkThemeSelector]: {
      [labelColor]: v.labelDark,
      [iconColor]: v.iconDark,
    },

    [selectors.hover]: {
      [labelColor]: v.labelHoverLight,
      [iconColor]: v.iconHoverLight,

      [darkThemeSelector]: {
        [labelColor]: v.labelHoverDark,
        [iconColor]: v.iconHoverDark,
      },
    },
  };
}
